<script lang="ts" setup>
    import { computed, onMounted, ref } from 'vue'
    import {
        useFachbereichStore,
        useModulHandbuchStore,
        useStudiengangStore,
        useAlertStore
    } from '@/js/stores'
    import { itemsPerPageOptions, itemsPerPageText, loadingText, noItemsText } from '@/js/utils'
    import { ModulHandbuch } from '@/js/model'

    const itemsPerPage = ref(100)

    const headers = ref([
        {
            key: 'semester',
            title: 'Semester'
        },
        {
            key: 'studiengang',
            title: 'Studiengang'
        },
        {
            key: 'fachbereich',
            title: 'Fachbereich'
        },
        {
            key: 'module',
            title: 'Module'
        },
        {
            key: 'actions',
            title: 'Aktionen',
            sortable: false,
            align: 'center'
        }
    ])

    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const modulHandbuchStore = useModulHandbuchStore()
    const alertStore = useAlertStore()

    const showConfirmRemovebDialog = ref(false)
    const modulhandbuchToRemove = ref<ModulHandbuch>()

    const modulHandBuecher = computed(() => modulHandbuchStore.getModulhandbuecher)
    const isLoading = computed(
        () =>
            modulHandbuchStore.isLoading || fachbereichStore.isLoading || studiengangStore.isLoading
    )
    const hasErrors = computed(
        () =>
            modulHandbuchStore.hasErrors || fachbereichStore.hasErrors || studiengangStore.hasErrors
    )
    const error = computed(
        () =>
            modulHandbuchStore.getErrors || fachbereichStore.getErrors || studiengangStore.getErrors
    )
    const confirmRemoveModulhandbuch = (item: ModulHandbuch) => {
        modulhandbuchToRemove.value = item
        showConfirmRemovebDialog.value = true
    }

    const removeModulhandbuch = async () => {
        if (modulhandbuchToRemove.value) {
            await modulHandbuchStore.removeModulhandbuch(modulhandbuchToRemove.value)
            showConfirmRemovebDialog.value = false
            if (!modulHandbuchStore.hasErrors) {
                alertStore.success('Modulhandbuch wurde entfernt.', 'Info')
            } else {
                alertStore.error('Fehler beim Entfernen des Modulhandbuchs.', 'Fehler!')
            }
        }
    }

    function getFormattedFachbereichFromStudiengangId(studiengangId: string): string {
        const studiengang = studiengangStore.getStudiengangById(studiengangId)
        if (!studiengang) {
            return ''
        }
        const fachbereich = fachbereichStore.getFachbereichByIri(studiengang.fachbereich)
        if (!fachbereich) {
            return ''
        }

        return `${fachbereich.kurzName} - ${fachbereich.name}`
    }

    function getStudiengangKurzName(studiengangId: string): string {
        const studiengang = studiengangStore.getStudiengangById(studiengangId)
        if (!studiengang) {
            return ''
        }
        return studiengang.kurzName
    }

    onMounted(() => {
        fachbereichStore.fetchFachbereiche()
        studiengangStore.fetchStudiengaenge()
        modulHandbuchStore.fetchModulHandbuecher()
    })
</script>

<template>
    <Error v-if="hasErrors" :error="error" class="mt-3" />
    <v-data-table-server
        :headers="headers"
        :items="modulHandBuecher"
        :items-per-page="itemsPerPage"
        :items-per-page-options="itemsPerPageOptions"
        :items-per-page-text="itemsPerPageText"
        :loading="isLoading"
        :loading-text="loadingText"
        :no-data-text="noItemsText"
        class="mb-0"
        data-test="studiengang-table"
        density="compact"
        disable-sort
        hide-default-footer
        items-length=""
        mobile-breakpoint="sm"
        multi-sort
    >
        <template #item.studiengang="{ item }">
            {{ getStudiengangKurzName(item.studiengangId) }}
        </template>
        <template #item.fachbereich="{ item }">
            {{ getFormattedFachbereichFromStudiengangId(item.studiengangId) }}
        </template>
        <template #item.module="{ item }">
            {{ item.modulIds.length }}
        </template>

        <template #item.actions="{ item }">
            <v-btn
                icon="mdi-delete"
                title="Modulhandbuch entfernen!"
                size="sm"
                @click="confirmRemoveModulhandbuch(item)"
            />
        </template>
    </v-data-table-server>

    <v-dialog v-model="showConfirmRemovebDialog" max-width="60%">
        <v-card>
            <v-card-title>
                Modulhandbuch
                {{ getFormattedFachbereichFromStudiengangId(modulhandbuchToRemove?.studiengangId) }}
                {{ modulhandbuchToRemove?.semester }} löschen?
            </v-card-title>
            <v-card-text>
                <div class="bg-info">
                    Hinweis: <br />
                    Alle Module des Modulhandbuchs, die keinen anderen Modulhandbüchern zugewiesen
                    sind, werden ebenfalls entfernt.
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn @click="removeModulhandbuch"> Löschen </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped></style>
